import { DictationCopilotMeta } from '../_interfaces/dictation';
import { formatDmyDate } from './dmy-date';
import { calculateAgeYears } from './utils';

export interface PersonNameParts {
  prefix?: string | null;
  first?: string | null;
  last?: string | null;
  flat?: string | null;
}

export interface PersonalDetails {
  name: string | null;
  dateOfBirth: string | null;
  age: string | null;
  sex: string | null;
  deceased: string | null;
}

export interface VisitDetails {
  specialty: string | null;
  location: string | null;
  admitted: string | null;
  discharged: string | null;
  consultant: string | null;
  referredBy: string | null;
}

export type ContextDetailsTranslate = (key: string, params?: Record<string, string | number>) => string;

const HL7_SEX: Record<string, string> = {
  m: 'Male',
  f: 'Female',
  o: 'Other',
  u: 'Unknown',
  a: 'Ambiguous',
  n: 'N/A',
};

export function presentText(value?: string | null): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  const lower = trimmed.toLowerCase();
  if (lower === 'null' || lower === 'undefined') return null;
  return trimmed;
}

/**
 * Compose Prefix First Last from split fields. The composite `flat` value is only
 * used when those parts are missing, and is normalised to the same order.
 */
export function formatPrefixedPersonName(parts: PersonNameParts): string | null {
  const prefix = presentText(parts.prefix);
  const first = presentText(parts.first);
  const last = presentText(parts.last);
  if (first || last) {
    const composed = [prefix, first, last].filter(Boolean).join(' ');
    if (composed) return composed;
  }
  const flat = presentText(parts.flat);
  return (flat ? normalizeCompositePersonName(flat) : null) ?? prefix;
}

export function normalizeCompositePersonName(value: string): string | null {
  const raw = presentText(value);
  if (!raw) return null;

  if (raw.includes('^')) {
    const segs = raw.split('^');
    const last = presentText(segs[1]);
    const first = presentText(segs[2]);
    const prefix = presentText(segs[5]);
    const composed = [prefix, first, last].filter(Boolean).join(' ');
    if (composed) return composed;
    const collapsed = segs.map(presentText).filter(Boolean).join(' ');
    return collapsed || null;
  }

  const withPrefix = raw.match(/^(.*?),\s*(.+?)\s*\(([^)]+)\)\s*$/);
  if (withPrefix) {
    return formatPrefixedPersonName({
      last: withPrefix[1],
      first: withPrefix[2],
      prefix: withPrefix[3],
    });
  }

  const lastFirst = raw.match(/^(.*?),\s*(.+)$/);
  if (lastFirst) {
    return formatPrefixedPersonName({
      last: lastFirst[1],
      first: lastFirst[2],
    });
  }

  return raw.replace(/\s+/g, ' ');
}

/** Administrative sex only — never used for pronouns or gender identity. */
export function normalizeSex(value: string): string | null {
  const raw = presentText(value);
  if (!raw) return null;

  const mapped = HL7_SEX[raw.toLowerCase()];
  if (mapped) return mapped;

  if (raw.length === 1) return null;

  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
}

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const UTC_MIDNIGHT = /^(\d{4})-(\d{2})-(\d{2})T00:00/;

function formatYmdParts(year: string, month: string, day: string): string | null {
  const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
  return isNaN(date.getTime()) ? null : formatDmyDate(date);
}

/** `dd/mm/yyyy`, with `HH:mm` appended when the source carries a time. */
export function formatContextDate(value: string): string | null {
  const raw = presentText(value);
  if (!raw) return null;

  const dateOnly = raw.match(DATE_ONLY) ?? raw.match(UTC_MIDNIGHT);
  if (dateOnly) {
    return formatYmdParts(dateOnly[1], dateOnly[2], dateOnly[3]);
  }

  const parsed = new Date(raw);
  if (isNaN(parsed.getTime())) return null;

  const hours = String(parsed.getHours()).padStart(2, '0');
  const minutes = String(parsed.getMinutes()).padStart(2, '0');
  return `${formatDmyDate(parsed)} ${hours}:${minutes}`;
}

export function formatAgeYears(dob: string): string | null {
  const age = calculateAgeYears(dob);
  if (age == null) return null;
  return String(age);
}

export function formatVisitSpeciality(visit: {
  visit_hospital_service?: string | null;
  visit_clinic_type_code?: string | null;
}): string | null {
  return presentText(visit.visit_hospital_service) || presentText(visit.visit_clinic_type_code);
}

export function mapPersonalDetails(
  copilot: DictationCopilotMeta,
  translate: ContextDetailsTranslate = (key) => key,
): PersonalDetails {
  const dob = presentText(copilot.subject_dob);
  const sexRaw = presentText(copilot.subject_sex);
  const sex = sexRaw ? normalizeSex(sexRaw) : null;
  const deathIndicator = presentText(copilot.subject_death_indicator);
  const deceased =
    deathIndicator?.toUpperCase() === 'Y' ? translate('Deceased') : null;

  return {
    name: formatPrefixedPersonName({
      prefix: copilot.subject_prefix,
      first: copilot.subject_first_name,
      last: copilot.subject_last_name,
      flat: copilot.subject_name,
    }),
    dateOfBirth: dob ? formatContextDate(dob) : null,
    age: dob ? formatAgeYears(dob) : null,
    sex: sex ? translate(sex) : null,
    deceased,
  };
}

export function mapVisitDetails(copilot: DictationCopilotMeta): VisitDetails {
  const admittedRaw = presentText(copilot.visit_admit_date_time);
  const dischargedRaw = presentText(copilot.visit_discharge_date_time);

  return {
    specialty: formatVisitSpeciality(copilot),
    location: presentText(copilot.visit_location),
    admitted: admittedRaw ? formatContextDate(admittedRaw) : null,
    discharged: dischargedRaw ? formatContextDate(dischargedRaw) : null,
    consultant: formatPrefixedPersonName({
      prefix: copilot.visit_consultant_doctor_prefix,
      first: copilot.visit_consultant_doctor_first_name,
      last: copilot.visit_consultant_doctor_last_name,
      flat: copilot.visit_consultant_doctor,
    }),
    referredBy: formatPrefixedPersonName({
      prefix: copilot.visit_referring_doctor_prefix,
      first: copilot.visit_referring_doctor_first_name,
      last: copilot.visit_referring_doctor_last_name,
      flat: copilot.visit_referring_doctor,
    }),
  };
}
